import {
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import {
  CircularProgress,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React from "react";
import { folderMappingsStorage } from "utils/localStorage";
import { FileItem as FileItemType } from "../../types/interfaces/filesStorage";

interface FileItemProps {
  item: FileItemType;
  onItemClick?: (item: FileItemType) => void;
  onDownloadClick?: (item: FileItemType) => void;
  showDownloadButton?: boolean;
  disabled?: boolean;
  isDownloading?: boolean;
  downloadProgress?: number;
  backgroundColor?: string;
}

const FileItem: React.FC<FileItemProps> = ({
  item,
  onItemClick,
  onDownloadClick,
  showDownloadButton = true,
  disabled = false,
  isDownloading = false,
  downloadProgress = 0,
  backgroundColor,
}) => {
  const isFolder = item.ftype === "folder";

  const handleItemClick = () => {
    if (!disabled && onItemClick) {
      onItemClick(item);
    }
  };

  const handleDownloadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled || isDownloading) return;

    if (onDownloadClick) {
      onDownloadClick(item);
    }
  };



  const renderIcon = () => {
    return (
      <ListItemIcon>
        {isFolder ? (
          <FolderIcon sx={{ color: "primary.main" }} />
        ) : (
          <FileIcon sx={{ color: "primary.main" }} />
        )}
      </ListItemIcon>
    );
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const renderText = () => {
    return (
      <>
        <ListItemText primary={item.fname} />
        {isFolder && (item.subfolder_count !== undefined || item.direct_file_count !== undefined) && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", ml: 2 }}>
            {[
              item.subfolder_count !== undefined && item.subfolder_count > 0
                ? `${item.subfolder_count} ${item.subfolder_count === 1 ? "folder" : "folders"}`
                : null,
              item.direct_file_count !== undefined && item.direct_file_count > 0
                ? `${item.direct_file_count} ${item.direct_file_count === 1 ? "file" : "files"}`
                : null,
            ]
              .filter(Boolean)
              .join(", ") || "0 folders"}
          </Typography>
        )}
        {!isFolder && item.last_modified && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", ml: 2 }}>
            {formatDate(item.last_modified)}
          </Typography>
        )}
      </>
    );
  };

  const renderActions = () => {
    // Count forward slashes in fsource
    const mappings = folderMappingsStorage.getFolderMappings();
    const keysCount = Object.keys(mappings).length;

    const shouldShowDownload =
      showDownloadButton &&
      keysCount >= 2;

    if (!shouldShowDownload) return null;

    return (
      <IconButton
        onClick={handleDownloadClick}
        disabled={disabled || isDownloading}
        className="text-gray-500 hover:text-gray-700"
      >
        {isDownloading ? (
          <CircularProgress
            size={16}
            variant="determinate"
            value={downloadProgress}
            sx={{
              color: downloadProgress === 100 ? 'success.main' : 'primary.main',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }}
          />
        ) : (
          <DownloadIcon />
        )}
      </IconButton>
    );
  };

  return (
    <ListItem
      button
      onClick={handleItemClick}
      disableGutters
      className="w-full"
      style={{
        padding: "4px 16px",
        backgroundColor: backgroundColor || "transparent"
      }}
    >
      {renderIcon()}
      {renderText()}
      {renderActions()}
    </ListItem>
  );
};

export default FileItem;
